import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { BackButton, Button } from '../components/common';
import { Heading, Text } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import { FormSection, FormField, Input } from '../components/common/Form';
import {
  resolvePasswordResetLink,
  isUsablePasswordResetLink,
  stripPasswordResetParams,
  mapPasswordResetSubmitError,
  markPasswordRecoverySession,
  promoteToRecoverySession,
  clearStoredPasswordResetLink,
  PASSWORD_RESET_LINK_MISSING_MESSAGE,
  PASSWORD_RESET_LINK_EXPIRED_MESSAGE,
} from '../utils/passwordReset';

const establishRecoverySession = async (link) => {
  if (link.kind === 'token_hash') {
    return supabase.auth.verifyOtp({
      token_hash: link.tokenHash,
      type: 'recovery',
    });
  }
  if (link.kind === 'implicit') {
    return supabase.auth.setSession({
      access_token: link.accessToken,
      refresh_token: link.refreshToken,
    });
  }
  if (link.kind === 'pkce') {
    return supabase.auth.exchangeCodeForSession(link.code);
  }
  if (link.kind === 'session_recovery') {
    const { data, error } = await supabase.auth.getSession();
    if (error) return { error };
    if (!data?.session) {
      return { error: new Error(PASSWORD_RESET_LINK_MISSING_MESSAGE) };
    }
    return { data, error: null };
  }
  return { error: new Error(PASSWORD_RESET_LINK_MISSING_MESSAGE) };
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [link, setLink] = useState(() => resolvePasswordResetLink());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    if (isUsablePasswordResetLink(link)) return null;
    if (link.kind === 'error') return PASSWORD_RESET_LINK_EXPIRED_MESSAGE;
    return PASSWORD_RESET_LINK_MISSING_MESSAGE;
  });
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    stripPasswordResetParams();
  }, []);

  useEffect(() => {
    if (isUsablePasswordResetLink(link)) return undefined;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event !== 'PASSWORD_RECOVERY') return;
      const recovery = markPasswordRecoverySession();
      setLink(recovery);
      setError(null);
    });
    return () => subscription.unsubscribe();
  }, [link]);

  const hasValidLink = isUsablePasswordResetLink(link);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!hasValidLink || loading) return;

    setLoading(true);
    setError(null);

    try {
      if (passwordData.password !== passwordData.confirmPassword) {
        throw new Error('Passwords do not match');
      }
      if (passwordData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      const { error: sessionError } = await establishRecoverySession(link);
      if (sessionError) {
        // PKCE / implicit links are often already exchanged by detectSessionInUrl.
        // token_hash is single-use: do not retry verifyOtp, and do not use a normal login session.
        if (link.kind === 'token_hash') throw sessionError;
        const { data } = await supabase.auth.getSession();
        if (!data?.session) throw sessionError;
      } else if (link.kind !== 'session_recovery') {
        // Token is spent after verify/exchange. Later submits (same-password retry) use this session.
        setLink(promoteToRecoverySession());
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordData.password,
      });
      if (updateError) throw updateError;

      clearStoredPasswordResetLink();

      const { error: signOutError } = await supabase.auth.signOut({ scope: 'global' });
      if (signOutError) {
        console.error('Error signing out after password reset:', signOutError);
      }

      navigate('/login', {
        replace: true,
        state: {
          loginFlash: 'Your password was updated. Please sign in with your new password.',
        },
      });
    } catch (err) {
      console.error('Error updating password:', err);
      const isValidation =
        err.message === 'Passwords do not match' ||
        err.message === 'Password must be at least 6 characters long';
      setError(isValidation ? err.message : mapPasswordResetSubmitError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Heading level={1} className="text-3xl font-bold text-text-primary mb-2">
            Reset Password
          </Heading>
          <Text className="text-text-secondary">
            {hasValidLink ? 'Enter your new password below' : 'This reset link cannot be used'}
          </Text>
        </div>

        <Card variant="default" size="large">
          {error && (
            <Card variant="danger" size="small" className="mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-danger-dark" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-danger-dark">{error}</p>
                </div>
              </div>
            </Card>
          )}

          {hasValidLink ? (
            <form onSubmit={handlePasswordReset} className="space-y-6">
              <FormSection>
                <FormField label="New Password" helperText="Password must be at least 6 characters long">
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={passwordData.password}
                    onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                    required
                    placeholder="Enter your new password"
                  />
                </FormField>

                <FormField label="Confirm New Password">
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                    placeholder="Confirm your new password"
                  />
                </FormField>
              </FormSection>

              <div className="flex flex-col space-y-3">
                <Button
                  type="submit"
                  disabled={loading}
                  variant="primary"
                  className="w-full"
                >
                  {loading ? 'Updating Password...' : 'Update Password'}
                </Button>

                <BackButton
                  onClick={() => navigate('/login')}
                  className="w-full"
                >
                  Back to Login
                </BackButton>
              </div>
            </form>
          ) : (
            <div className="flex flex-col space-y-3">
              <BackButton
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Back to Login
              </BackButton>
              <p className="text-center text-sm text-text-secondary">
                Use &quot;Forgot password?&quot; on the login page to request a new link, and open it in the same window — not a new private/incognito window.
              </p>
            </div>
          )}
        </Card>

        <div className="text-center">
          <Text className="text-sm text-text-muted">
            Need help? Contact support for assistance.
          </Text>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
